import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import DataTable from "../../../components/DataTable/DataTable";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useNavigate} from "react-router-dom";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {Region, RegionsResponse} from "../../../types/Responses/RegionsResponse";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../../reducers/sidebar";

const Regions = () => {
    const {t} = useTranslation();
    const user = useUserContext();
    const navigate = useNavigate();
    const toast = useToastContext();
    const dispatch = useDispatch();
    const handleError = useErrorHandler();
    const [regions, setRegions] = useState<RegionsResponse>();
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const defaultRequest = useMemo<DataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<DataTableRequest>(defaultRequest);

    const sendRequest = useCallback((request: DataTableRequest) => {
        setIsDataTableLoading(true);
        API.getRegions(request).then((response: AxiosResponse) => {
            setRegions(response.data as RegionsResponse);
            setIsDataTableLoading(false);
        }).catch((e) => ErrorHandler(e, `get${APILink.GET_REGIONS}`, toast, handleError, setIsForbidden));
    }, [toast, handleError, setIsDataTableLoading]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        persistDataTableRequest(request);
        if (!request.search || request.search.length > 3) {
            sendRequest(!request.search ? {...request, search: ''} : request);
        }
    }, [persistDataTableRequest, sendRequest]);

    const deleteRegion = useCallback((region: Region) => {
        if (regions) {
            const filteredArray = {
                ...regions,
                data: regions.data.filter(r => r.id !== region.id),
                count: regions.count - 1
            };
            setIsDataTableLoading(true);
            API.deleteRegion(region.id).then(() => {
                if (region) {
                    setRegions(filteredArray);
                    setIsDataTableLoading(false);
                }
            }).catch(e => ErrorHandler(e, `delete${APILink.GET_REGION.replace('$region', region.id?.toString() ?? '')}`, toast, handleError));
        }
    }, [regions, setIsDataTableLoading, setRegions, toast, handleError]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);


    useEffect(() => {
        setTitle(t('admin.regions.title'));
        dispatch(setSidebarTitle(t('admin.regions.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        const request = {...dataTableRequest, search: newID[1]};
                        persistDataTableRequest(request);
                        sendRequest(request);
                    }
                } else {
                    sendRequest(dataTableRequest);
                }
            }).catch();
        }
    }, [t, user, dispatch]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
                {user?.user.permissions.includes('create region') &&
                    <Button onClick={() => navigate('/app/admin/regions/create')}
                            className="bg-black text-white">{t('admin.regions.new.button')}</Button>}
            </div>
        )
    }, [user?.user.permissions, t, refresh, navigate]);

    const dataTable = useMemo(() => {
        if (!regions)
            return <></>;

        return <DataTable {...dataTableRequest}
                          total_pages={regions.pages.total}
                          count={regions.count}
                          nodata={regions.data.length === 0}
                          rightPanel={actionsPanel}
                          inProgress={isDataTableLoading}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t("admin.regions.table.name"), sortBy: 'name'},
            {name: t("admin.regions.table.countries")},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {regions.data.map((region, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD>{region.name}</TD>
                        <TD>{region.countries.length}</TD>
                        <TD className="text-right">
                            <>
                                {user?.user.permissions.includes('update region') &&
                                    <Button onClick={() => navigate(`/app/admin/regions/${region.id}`)}
                                            className="px-5 py-2 text-white bg-black lg:mr-2"><FontAwesomeIcon
                                        icon={solid("pen-to-square")}/></Button>}
                                {user?.user.permissions.includes('delete region') &&
                                    <Button onClick={() => deleteRegion(region)}
                                            className="px-5 py-2 text-red-300 bg-black"><FontAwesomeIcon
                                        icon={solid("trash")}/></Button>}
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, isDataTableLoading, regions, deleteRegion, navigate, onDataTableChanged, user?.user.permissions]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {dataTable}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Regions);
